import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  Search,
  X,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  Route as RouteIcon,
  BookOpen,
  Layers,
  FileText,
  Library,
  ShieldCheck,
  Users,
  Sparkles,
  Clock,
  Loader2,
} from "lucide-react";
import {
  useCosmosClient,
  type GlobalSearchCategory,
  type GlobalSearchResponse,
  type GlobalSearchResultCategory,
  type GlobalSearchResultItem,
} from "@/lib/cosmos-api";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";
import {
  mergeRecentSearches,
  readRecentSearches,
  rememberRecentSearch,
  resolveRecentSearchScope,
  writeRecentSearches,
  type StoredRecentSearch,
} from "@/lib/recent-searches";

const SEARCH_DEBOUNCE_MS = 250;

const categories: GlobalSearchCategory[] = [
  "All",
  "Journeys",
  "Courses",
  "Lessons",
  "Modules",
  "Blog Posts",
  "Resources Library",
  "Licenses",
  "Members",
];

const typeMeta: Record<
  GlobalSearchResultCategory,
  { icon: typeof Search; tone: string }
> = {
  Journeys: { icon: RouteIcon, tone: "bg-[var(--violet)]/15 text-[var(--violet)]" },
  Courses: { icon: BookOpen, tone: "bg-[var(--primary)]/12 text-[var(--primary)]" },
  Lessons: { icon: BookOpen, tone: "bg-[var(--azure)]/15 text-[var(--azure)]" },
  Modules: { icon: Layers, tone: "bg-[var(--success)]/15 text-[var(--success)]" },
  "Blog Posts": { icon: FileText, tone: "bg-muted text-anchor" },
  "Resources Library": { icon: Library, tone: "bg-[var(--warning)]/15 text-[var(--warning)]" },
  Licenses: { icon: ShieldCheck, tone: "bg-[var(--primary)]/12 text-[var(--primary)]" },
  Members: { icon: Users, tone: "bg-muted text-anchor" },
};

const groupedOrder: GlobalSearchResultCategory[] = [
  "Journeys",
  "Courses",
  "Lessons",
  "Modules",
  "Blog Posts",
  "Resources Library",
  "Licenses",
  "Members",
];

function highlight(text: string, q: string) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-[var(--primary)] font-semibold">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}

export function GlobalSearch({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const cosmos = useCosmosClient();
  const auth = useAihrAuth();
  const recentScope = useMemo(
    () =>
      resolveRecentSearchScope({
        tenantId: "default-tenant",
        userId: auth.authEnabled ? auth.user?.email ?? "anonymous" : "local-cosmos-user",
      }),
    [auth.authEnabled, auth.user?.email],
  );
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<GlobalSearchCategory>("All");
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState<StoredRecentSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<GlobalSearchResponse | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastRecordedRef = useRef<string | null>(null);

  const persistRecentSearch = useCallback(
    async (query: string, searchCategory: GlobalSearchCategory) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return;
      }
      const recordKey = `${trimmed.toLowerCase()}::${searchCategory}`;
      if (lastRecordedRef.current === recordKey) {
        return;
      }
      lastRecordedRef.current = recordKey;

      const local = rememberRecentSearch(recentScope, trimmed, searchCategory);
      setRecent(local);

      try {
        const recorded = await cosmos.recordRecentSearch({
          query: trimmed,
          category: searchCategory,
        });
        setRecent((current) => {
          const merged = mergeRecentSearches(current, [recorded]);
          writeRecentSearches(recentScope, merged);
          return merged;
        });
      } catch {
        // Keep the local fallback when the API is unavailable.
      }
    },
    [cosmos, recentScope],
  );

  const runSearch = useCallback(
    async (query: string, activeCategory: GlobalSearchCategory) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setSearchResult(null);
        setLoading(false);
        setError(null);
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);

      try {
        const response = await cosmos.search({
          query: trimmed,
          category: activeCategory,
          limit: activeCategory === "All" ? undefined : 20,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setSearchResult(response);
          void persistRecentSearch(trimmed, activeCategory);
        }
      } catch (searchError) {
        if (controller.signal.aborted) return;
        const message =
          searchError instanceof Error ? searchError.message : "Search failed.";
        setError(message);
        setSearchResult(null);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [cosmos, persistRecentSearch],
  );

  useEffect(() => {
    if (open) {
      setQ("");
      setCategory("All");
      setActiveIdx(0);
      setSearchResult(null);
      setError(null);
      lastRecordedRef.current = null;
      const local = readRecentSearches(recentScope);
      setRecent(local);
      void cosmos
        .listRecentSearches()
        .then((serverRecent) => {
          const merged = mergeRecentSearches(local, serverRecent);
          setRecent(merged);
          writeRecentSearches(recentScope, merged);
        })
        .catch(() => {
          // Keep cached searches when the API is unavailable.
        });
      setTimeout(() => inputRef.current?.focus(), 30);
    }
    return () => abortRef.current?.abort();
  }, [open, cosmos, recentScope]);

  useEffect(() => {
    setActiveIdx(0);
    if (!q.trim()) {
      abortRef.current?.abort();
      setSearchResult(null);
      setLoading(false);
      setError(null);
      return;
    }
    const timer = window.setTimeout(() => {
      void runSearch(q, category);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [q, category, runSearch]);

  const counts = useMemo(() => {
    const empty = groupedOrder.reduce(
      (accumulator, entry) => {
        accumulator[entry] = 0;
        return accumulator;
      },
      {} as Record<GlobalSearchResultCategory, number>
    );
    return searchResult?.counts ?? empty;
  }, [searchResult]);

  const grouped = useMemo(() => {
    if (category !== "All" || !searchResult) return null;
    if (searchResult.grouped) {
      return groupedOrder
        .map((type) => ({
          type,
          items: searchResult.grouped?.[type] ?? [],
        }))
        .filter((group) => group.items.length > 0);
    }
    return groupedOrder
      .map((type) => ({
        type,
        items: searchResult.results.filter((result) => result.category === type).slice(0, 3),
      }))
      .filter((group) => group.items.length > 0);
  }, [category, searchResult]);

  const flatList = useMemo(() => {
    if (grouped) return grouped.flatMap((group) => group.items);
    return searchResult?.results ?? [];
  }, [grouped, searchResult]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const navigateToResult = (result: GlobalSearchResultItem) => {
    onOpenChange(false);
    if (result.external || /^https?:\/\//i.test(result.href)) {
      window.open(result.href, "_blank", "noopener,noreferrer");
      return;
    }
    navigate({ to: result.href as never });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(flatList.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const i = categories.indexOf(category);
      setCategory(categories[Math.max(i - 1, 0)]);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      const i = categories.indexOf(category);
      setCategory(categories[Math.min(i + 1, categories.length - 1)]);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = flatList[activeIdx];
      if (sel) {
        navigateToResult(sel);
      } else if (q.trim()) {
        onOpenChange(false);
        navigate({ to: "/workspace" });
      }
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[color:var(--anchor)]/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onKeyDown={onKeyDown}
          className={cn(
            "fixed left-1/2 top-[12vh] z-50 w-[min(720px,92vw)] -translate-x-1/2",
            "bg-surface text-popover-foreground rounded-2xl overflow-hidden",
            "shadow-[0_40px_100px_-20px_rgba(23,15,63,0.45),0_12px_32px_-12px_rgba(23,15,63,0.2)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">Search Cosmos</DialogPrimitive.Title>

          <div className="flex items-center gap-3 px-5 h-14">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search journeys, courses, lessons, licenses, members…"
              className="flex-1 bg-transparent outline-none text-[14px] text-anchor placeholder:text-muted-foreground"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
            {q && (
              <button
                onClick={() => setQ("")}
                className="text-[11px] text-muted-foreground hover:text-anchor"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-anchor"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-3 pb-2.5 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-1 min-w-max">
              {categories.map((c) => {
                const active = c === category;
                const count =
                  c === "All"
                    ? Object.values(counts).reduce((a, b) => a + b, 0)
                    : counts[c as GlobalSearchResultCategory];
                return (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12px] font-medium transition-colors",
                      active
                        ? "bg-anchor text-white"
                        : "text-muted-foreground hover:bg-muted hover:text-anchor",
                    )}
                  >
                    {c}
                    {q && (
                      <span
                        className={cn(
                          "text-[10px] tabular-nums",
                          active ? "text-white/70" : "text-muted-foreground/70",
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div ref={listRef} className="max-h-[52vh] overflow-y-auto no-scrollbar">
            {!q && (
              <div className="px-5 py-3">
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground tracking-wide mb-2">
                  <Clock className="h-3 w-3" />
                  RECENT SEARCHES
                </div>
                {recent.length === 0 ? (
                  <div className="text-[12px] text-muted-foreground">
                    Search learning content, blog posts, licenses, and members.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {recent.map((entry) => (
                      <button
                        key={`${entry.query}:${entry.category}`}
                        onClick={() => {
                          setQ(entry.query);
                          setCategory(entry.category);
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md bg-muted/60 text-[12px] text-anchor hover:bg-muted"
                      >
                        <span>{entry.query}</span>
                        {entry.category !== "All" && (
                          <span className="text-[10px] text-muted-foreground">
                            {entry.category}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && q && (
              <div className="px-5 py-8 text-center text-[12px] text-red-600">{error}</div>
            )}

            {!error && q && !loading && flatList.length === 0 && (
              <div className="px-5 py-14 text-center">
                <div className="mx-auto h-10 w-10 rounded-full bg-muted grid place-items-center mb-3">
                  <Search className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="text-sm font-medium text-anchor">No matches for "{q}"</div>
                <div className="text-[12px] text-muted-foreground mt-1">
                  Try asking Cosmos in the workspace instead.
                </div>
                <button
                  onClick={() => {
                    onOpenChange(false);
                    navigate({ to: "/workspace" });
                  }}
                  className="mt-4 inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[var(--primary)] text-[color:var(--primary-foreground)] text-[12px] font-medium hover:opacity-90"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Ask Cosmos
                </button>
              </div>
            )}

            {flatList.length > 0 && grouped ? (
              <div className="pb-2">
                {grouped.map((group) => {
                  const total = counts[group.type];
                  return (
                    <div key={group.type}>
                      <div className="flex items-center justify-between px-5 pt-3.5 pb-1.5">
                        <div className="text-[11px] font-medium text-muted-foreground tracking-wide">
                          {group.type}
                          <span className="ml-1.5 text-muted-foreground/70 tabular-nums">{total}</span>
                        </div>
                        {total > group.items.length && (
                          <button
                            onClick={() => setCategory(group.type)}
                            className="text-[11px] font-medium text-[var(--primary)] hover:underline"
                          >
                            See all
                          </button>
                        )}
                      </div>
                      <ul>
                        {group.items.map((result) => {
                          const idx = flatList.indexOf(result);
                          return (
                            <ResultRow
                              key={result.id}
                              r={result}
                              q={q}
                              active={idx === activeIdx}
                              idx={idx}
                              onSelect={() => navigateToResult(result)}
                              onHover={() => setActiveIdx(idx)}
                            />
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            ) : flatList.length > 0 ? (
              <ul className="py-2">
                {flatList.map((result, idx) => (
                  <ResultRow
                    key={result.id}
                    r={result}
                    q={q}
                    active={idx === activeIdx}
                    idx={idx}
                    onSelect={() => navigateToResult(result)}
                    onHover={() => setActiveIdx(idx)}
                  />
                ))}
              </ul>
            ) : null}
          </div>

          <div className="flex items-center justify-between px-5 h-10 bg-background/60 text-[11px] text-muted-foreground">
            <div className="tabular-nums">
              {flatList.length} result{flatList.length === 1 ? "" : "s"} found
            </div>
            <div className="flex items-center gap-3">
              <KeyHint><ArrowUp className="h-3 w-3" /></KeyHint>
              <KeyHint><ArrowDown className="h-3 w-3" /></KeyHint>
              <span>Navigate</span>
              <KeyHint><CornerDownLeft className="h-3 w-3" /></KeyHint>
              <span>Select</span>
              <KeyHint>esc</KeyHint>
              <span>Close</span>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ResultRow({
  r, q, active, idx, onSelect, onHover,
}: {
  r: GlobalSearchResultItem; q: string; active: boolean; idx: number; onSelect: () => void; onHover: () => void;
}) {
  const meta = typeMeta[r.category];
  const Icon = meta.icon;
  return (
    <li>
      <button
        data-idx={idx}
        onMouseEnter={onHover}
        onClick={onSelect}
        className={cn(
          "w-full flex items-center gap-3 px-5 py-2 text-left transition-colors",
          active ? "bg-muted/70" : "hover:bg-muted/40",
        )}
      >
        <div className={cn("h-8 w-8 shrink-0 rounded-lg grid place-items-center", meta.tone)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-medium text-anchor truncate">
              {highlight(r.title, q)}
            </div>
            {r.badge && (
              <span className="shrink-0 inline-flex items-center px-1.5 h-[16px] rounded-full bg-muted text-[10px] font-medium text-anchor">
                {r.badge}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-muted-foreground truncate">{r.subtitle}</div>
        </div>
        {active && (
          <span className="shrink-0 text-muted-foreground">
            <CornerDownLeft className="h-3.5 w-3.5" />
          </span>
        )}
      </button>
    </li>
  );
}

function KeyHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded border border-[color:var(--border-soft)] bg-surface text-[10px] font-medium text-anchor">
      {children}
    </kbd>
  );
}
